import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Share2,
  Copy,
  ExternalLink,
  Eye,
  Code,
  Loader2,
  Trash2,
  Monitor,
  Palette,
  Bell,
  BarChart3,
  Trophy,
  TrendingUp,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const THEME_OPTIONS = [
  { id: 'dark', name: 'Dark', colors: { bg: '#0f172a', accent: '#10b981' } },
  { id: 'light', name: 'Light', colors: { bg: '#f8fafc', accent: '#10b981' } },
  { id: 'midnight', name: 'Midnight', colors: { bg: '#020617', accent: '#8b5cf6' } },
  { id: 'sunset', name: 'Sunset', colors: { bg: '#1c1917', accent: '#f97316' } },
  { id: 'ocean', name: 'Ocean', colors: { bg: '#0c4a6e', accent: '#06b6d4' } },
  { id: 'forest', name: 'Forest', colors: { bg: '#14532d', accent: '#22c55e' } },
  { id: 'transparent', name: 'Transparent', colors: { bg: 'transparent', accent: '#10b981' } },
];

const widgetTypes = [
  { id: 'portfolio', name: 'Portfolio', icon: Monitor, desc: 'Live portfolio with holdings' },
  { id: 'leaderboard', name: 'Leaderboard', icon: Trophy, desc: 'Your rank & top players' },
  { id: 'performance', name: 'Performance', icon: TrendingUp, desc: 'P&L with weekly chart' },
  { id: 'ticker', name: 'Ticker', icon: BarChart3, desc: 'Minimal overlay for streams' },
];

export const StreamShareTab = ({ user, token, settings, fetchSettings }) => {
  const [generating, setGenerating] = useState(false);
  const [customTitle, setCustomTitle] = useState(settings?.custom_title || '');
  const [webhookUrl, setWebhookUrl] = useState(settings?.webhook_url || '');
  const [selectedTheme, setSelectedTheme] = useState(settings?.theme_preset || 'dark');
  const [activeWidgetTab, setActiveWidgetTab] = useState('portfolio');

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const generateShareLink = async () => {
    setGenerating(true);
    try {
      await fetch(`${API_URL}/api/creator/generate-share-link`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Share link generated!');
      fetchSettings();
    } catch {
      toast.error('Failed to generate share link');
    } finally {
      setGenerating(false);
    }
  };

  const updateSettings = async (updates) => {
    try {
      const params = new URLSearchParams(updates);
      const res = await fetch(`${API_URL}/api/creator/share-settings?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Settings updated');
        fetchSettings();
      }
    } catch {
      toast.error('Failed to update settings');
    }
  };

  const deleteShareLink = async () => {
    if (!confirm('Are you sure? This will disable all your shared widgets.')) return;
    try {
      const res = await fetch(`${API_URL}/api/creator/share-link`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Share link deleted');
        fetchSettings();
      }
    } catch {
      toast.error('Failed to delete share link');
    }
  };

  const testWebhook = async () => {
    try {
      const res = await fetch(`${API_URL}/api/creator/test-webhook`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Test notification sent!');
      } else {
        toast.error(data.message);
      }
    } catch {
      toast.error('Failed to test webhook');
    }
  };

  const getWidgetUrl = (type) => {
    if (!settings?.share_code) return '';
    return `${API_URL}/api/creator/widget/${type}/${settings.share_code}?theme=${selectedTheme}`;
  };

  const getEmbedCode = (type) => {
    const url = getWidgetUrl(type);
    const sizes = {
      portfolio: { w: 400, h: 300 },
      leaderboard: { w: 350, h: 400 },
      performance: { w: 400, h: 320 },
      ticker: { w: 500, h: 60 }
    };
    const size = sizes[type] || { w: 400, h: 300 };
    return `<iframe src="${url}" width="${size.w}" height="${size.h}" frameborder="0" style="border:none;"></iframe>`;
  };

  if (!settings?.has_share_link) {
    return (
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="py-12 text-center">
          <Monitor className="h-16 w-16 mx-auto text-slate-600 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Get Started with Streaming</h3>
          <p className="text-slate-400 mb-6 max-w-md mx-auto">
            Generate streaming widgets, share your portfolio publicly, and set up trade notifications for your audience.
          </p>
          <Button
            onClick={generateShareLink}
            disabled={generating}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="generate-share-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Share2 className="h-4 w-4 mr-2" />}
            Generate Share Link
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* Widget Selection */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Code className="h-5 w-5 text-emerald-500" />
            Streaming Widgets
          </CardTitle>
          <CardDescription>Choose a widget for OBS or your stream</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {widgetTypes.map(w => (
              <button
                key={w.id}
                onClick={() => setActiveWidgetTab(w.id)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  activeWidgetTab === w.id
                    ? 'bg-emerald-500/20 border-emerald-500 text-white'
                    : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
                }`}
              >
                <w.icon className={`h-5 w-5 mb-1 ${activeWidgetTab === w.id ? 'text-emerald-500' : 'text-slate-500'}`} />
                <div className="font-medium text-sm">{w.name}</div>
                <div className="text-xs text-slate-500">{w.desc}</div>
              </button>
            ))}
          </div>

          <div className="mt-4">
            <Label className="text-slate-300 mb-2 block">Preview</Label>
            <div className="bg-slate-900 rounded-lg p-2 border border-slate-700">
              <iframe
                src={getWidgetUrl(activeWidgetTab)}
                className="w-full rounded"
                style={{ height: activeWidgetTab === 'ticker' ? '80px' : '280px' }}
                title="Widget Preview"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(getWidgetUrl(activeWidgetTab), 'Widget URL')} className="flex-1">
              <Copy className="h-4 w-4 mr-2" /> Copy URL
            </Button>
            <Button variant="outline" size="sm" onClick={() => copyToClipboard(getEmbedCode(activeWidgetTab), 'Embed code')} className="flex-1">
              <Code className="h-4 w-4 mr-2" /> Copy Embed
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(getWidgetUrl(activeWidgetTab), '_blank')}>
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Theme Selection */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Palette className="h-5 w-5 text-emerald-500" />
            Widget Theme
          </CardTitle>
          <CardDescription>Match your stream's branding</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {THEME_OPTIONS.map(theme => (
              <button
                key={theme.id}
                onClick={() => {
                  setSelectedTheme(theme.id);
                  updateSettings({ theme_preset: theme.id });
                }}
                className={`p-2 rounded-lg border text-center transition-all ${
                  selectedTheme === theme.id
                    ? 'border-emerald-500 ring-2 ring-emerald-500/30'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
              >
                <div
                  className="w-full h-8 rounded mb-1"
                  style={{
                    background: theme.colors.bg === 'transparent'
                      ? 'repeating-conic-gradient(#333 0% 25%, #222 0% 50%) 50% / 10px 10px'
                      : theme.colors.bg
                  }}
                />
                <div className="text-xs text-slate-400">{theme.name}</div>
              </button>
            ))}
          </div>

          <div className="pt-4 border-t border-slate-700">
            <Label className="text-slate-300 flex items-center gap-2 mb-2">
              <RefreshCw className="h-4 w-4" /> Auto-refresh Rate
            </Label>
            <div className="flex gap-2">
              {[15, 30, 60].map(sec => (
                <Button
                  key={sec}
                  variant={settings?.auto_refresh_seconds === sec ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateSettings({ auto_refresh_seconds: sec })}
                  className={settings?.auto_refresh_seconds === sec ? 'bg-emerald-600' : ''}
                >
                  {sec}s
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Display Settings */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Eye className="h-5 w-5 text-emerald-500" />
            Display Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Custom Title</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder={`${user?.username}'s Portfolio`}
                className="bg-slate-900 border-slate-700"
              />
              <Button variant="outline" onClick={() => updateSettings({ custom_title: customTitle })}>
                Save
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300">Show Holdings</Label>
              <p className="text-xs text-slate-500">Display individual positions</p>
            </div>
            <Switch checked={settings?.show_holdings} onCheckedChange={(v) => updateSettings({ show_holdings: v })} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300">Show Performance</Label>
              <p className="text-xs text-slate-500">Display weekly scores</p>
            </div>
            <Switch checked={settings?.show_performance} onCheckedChange={(v) => updateSettings({ show_performance: v })} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300">Share Link Active</Label>
              <p className="text-xs text-slate-500">Enable/disable all widgets</p>
            </div>
            <Switch checked={settings?.is_active} onCheckedChange={(v) => updateSettings({ is_active: v })} />
          </div>
        </CardContent>
      </Card>

      {/* Webhook / Trade Alerts */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-500" />
            Trade Alerts
          </CardTitle>
          <CardDescription>Get notified via Discord/Slack webhook</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-slate-300">Webhook URL</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
                className="bg-slate-900 border-slate-700"
                type="url"
              />
              <Button variant="outline" onClick={() => updateSettings({ webhook_url: webhookUrl })}>
                Save
              </Button>
            </div>
          </div>

          {settings?.webhook_url && (
            <Button variant="outline" size="sm" onClick={testWebhook} className="w-full">
              <Bell className="h-4 w-4 mr-2" /> Send Test Notification
            </Button>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300">Trade Notifications</Label>
              <p className="text-xs text-slate-500">Alert when you make trades</p>
            </div>
            <Switch checked={settings?.notify_trades} onCheckedChange={(v) => updateSettings({ notify_trades: v })} />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-slate-300">Rank Changes</Label>
              <p className="text-xs text-slate-500">Alert on leaderboard moves</p>
            </div>
            <Switch checked={settings?.notify_rank_change} onCheckedChange={(v) => updateSettings({ notify_rank_change: v })} />
          </div>
        </CardContent>
      </Card>

      {/* Stats & Danger Zone */}
      <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-slate-400 text-sm">Total Views</span>
                <p className="text-2xl font-bold text-white">{settings?.views || 0}</p>
              </div>
              <div className="w-px h-10 bg-slate-700" />
              <div>
                <span className="text-slate-400 text-sm">Share Code</span>
                <p className="text-lg font-mono text-emerald-400">{settings?.share_code}</p>
              </div>
            </div>
            <Button variant="destructive" size="sm" onClick={deleteShareLink}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete Share Link
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
